import React from "react";
import {
  AspectRatio,
  AvatarGroup,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  Text,
  VStack,
  useToast,
  } from "@chakra-ui/react";
import { BrandAvatar } from "../../components/BrandAvatar";
import {
  FiBookOpen,
  FiCalendar,
  FiFileText,
  FiFlag,
  FiHeart,
  FiMessageSquare,
  FiSend,
  FiVideo,
  FiTrash2,
  FiClipboard,
} from "react-icons/fi";
import { API_BASE, DEFAULT_HEADERS } from "../../services/apiClient";
import { profilePath } from "../../services/profilePath";
import {
  getCachedUserBundle,
  getCurrentUserId,
} from "../../services/authService";
import { toIntId } from "../hooks/useLikes";
import { formatFeedDate } from "../utils/date";
import { renderTextWithMentions, buildMemberIndex } from "../utils/mentions";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { openExternalProfile } from "../../services/openExternalProfile";
import { Tag, TagLabel, Link } from "@chakra-ui/react";
import { getJournalLogoPath, JOURNAL_GENERIC_ICON } from "../../utils/journalLogos";
import { MentionTextarea, loadUsers } from "./MentionTextarea";


// se FeedList è in src/dashboard/components/, questo path è giusto.
// Se non lo trova, aggiusta di uno ../



const isProd = process.env.NODE_ENV === "production";
const debugLog = (...args) => {
  if (!isProd) {
    // eslint-disable-next-line no-console
    console.log("[FeedList]", ...args);
  }
};

const getPublicationLogo = (item) => {
  if (
    item?.journal_logo &&
    typeof item.journal_logo === "string" &&
    item.journal_logo.trim()
  ) {
    return item.journal_logo.trim();
  }
  return getJournalLogoPath(item?.journal || item?.publisher || "");
};

const typeMeta = (t) => {
  switch (t) {
    case "news":
      return { icon: FiFileText, label: "dashboard.news" };
    case "conference":
      return { icon: FiCalendar, label: "dashboard.event" };
    case "publication":
      return { icon: FiBookOpen, label: "dashboard.article" };
    case "video":
      return { icon: FiVideo, label: "dashboard.video_label" };
    case "notice":
      return { icon: FiClipboard, label: "dashboard.notices" };
    default:
      return { icon: FiFileText, label: "dashboard.post_label" };
  }
};

const makeKey = (item, idx) => {
  const cand =
    item?.id ||
    item?._id ||
    item?.guid ||
    item?.url ||
    item?.link ||
    item?.href ||
    item?.permalink ||
    item?.title ||
    item?.name ||
    item?.journal ||
    item?.source ||
    item?.pub_date ||
    item?.date ||
    "item";
  return `${item?.type || "item"}:${String(cand)}:${idx}`;
};

const getLink = (it) => {
  const u =
    it?.url ||
    it?.link ||
    it?.website ||
    it?.source_url ||
    it?.href ||
    it?.permalink ||
    null;
  if (!u || typeof u !== "string" || !u.trim()) return null;
  return u.trim();
};

const PROJECT_PUBLICATION_MARKER = "[Collaborative Project]";
const PROJECT_INTEREST_MARKER = "[PROJECT_INTEREST]";

const getProjectPublicationMeta = (item) => {
  if (!item || item.type !== "post") return null;
  const subtype = String(item.post_subtype || "").toLowerCase();
  const rawBody = typeof item.body === "string" ? item.body.trim() : "";
  const lines = rawBody
    ? rawBody
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
    : [];
  const hasMarker =
    lines.length > 0 && lines[0].startsWith(PROJECT_PUBLICATION_MARKER);
  const isProjectPublication =
    subtype === "project_publication" ||
    subtype === "collaborative_project" ||
    hasMarker;

  if (!isProjectPublication) return null;

  const fieldMap = {};
  lines.slice(hasMarker ? 1 : 0).forEach((line) => {
    const sep = line.indexOf(":");
    if (sep <= 0) return;
    const rawKey = line.slice(0, sep).trim().toLowerCase().replace(/\s+/g, "_");
    const value = line.slice(sep + 1).trim();
    if (!rawKey || !value) return;
    fieldMap[rawKey] = value;
  });

  const titleFromMarker =
    hasMarker && lines[0].length > PROJECT_PUBLICATION_MARKER.length
      ? lines[0].slice(PROJECT_PUBLICATION_MARKER.length).trim()
      : "";

  return {
    title: titleFromMarker || item.title || "Collaborative Project",
    theme: fieldMap.theme || item.theme || "",
    regionalCluster:
      fieldMap.regional_cluster || item.regional_cluster || "Global / Multi-region",
    scope: fieldMap.scope || item.project_scope || "",
    track: fieldMap.track || item.project_track || "",
    summary: fieldMap.summary || "",
    routing: fieldMap.routing || item.routing_target || "",
  };
};

const resolvePostOwnerId = (item) =>
  item?.user_id ||
  item?.userId ||
  item?.user?.id ||
  // What /feed/posts calls the author's id.
  item?.author_user_id ||
  item?.author?.id ||
  item?.author_id ||
  item?.owner_id ||
  null;

const createInterestMessage = ({ projectMeta, draft, applicantName }) =>
  [
    `${PROJECT_INTEREST_MARKER} ${projectMeta?.title || "Collaborative Project"}`,
    `Applicant: ${applicantName || "GWBI Member"}`,
    `Theme: ${projectMeta?.theme || "Not specified"}`,
    `Regional Cluster: ${projectMeta?.regionalCluster || "Global / Multi-region"}`,
    `Motivation: ${draft?.motivation || ""}`,
    `Expertise: ${draft?.expertise || "Not provided"}`,
    `Data / Methods Contribution: ${draft?.dataContribution || "Not provided"}`,
    `Availability: ${draft?.availability || "Not provided"}`,
  ].join("\n");

// A paper, as it appears in the feed.
//
// Before this, a paper announcement was the raw body text the server writes --
// "New paper added:", the authors on the next line, then a bare URL. Every
// reader saw that; only the author ever saw a formatted version, because it
// was built from a separate call for their own documents. This is the one card
// everybody gets.
function PaperCard({ doc, expanded, onToggle, t }) {
  const host = (() => {
    try {
      return new URL(doc.url).hostname.replace(/^www\./, "");
    } catch (_) {
      return null;
    }
  })();
  const isPdf =
    (doc.content_type || "").includes("pdf") || /\.pdf($|\?)/i.test(doc.url || "");
  const year = (doc.publication_date || "").slice(0, 4);
  const summary = doc.insight || doc.abstract;

  return (
    <Box
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="ui-lg"
      overflow="hidden"
      bg="surface.canvas"
    >
      <HStack align="stretch" spacing={0}>
        {doc.thumbnail_url ? (
          <Image
            src={doc.thumbnail_url}
            alt=""
            w="76px"
            minH="100%"
            objectFit="cover"
            flexShrink={0}
            borderRightWidth="1px"
            borderColor="border.default"
          />
        ) : (
          // No thumbnail: the kind of thing it is, rather than a broken image.
          <Flex
            w="76px"
            flexShrink={0}
            align="center"
            justify="center"
            bg="gwbi.50"
            borderRightWidth="1px"
            borderColor="border.default"
          >
            <Icon as={FiFileText} boxSize={6} color="gwbi.600" />
          </Flex>
        )}

        <Box p={4} flex="1" minW={0}>
          <HStack spacing={1.5} mb={1} color="gwbi.600">
            <Text
              fontSize="2xs"
              fontWeight="bold"
              letterSpacing="0.14em"
              textTransform="uppercase"
            >
              {t("dashboard.new_paper", "New paper")}
            </Text>
            {year && (
              <Text fontSize="2xs" color="text.muted">
                · {year}
              </Text>
            )}
          </HStack>

          <Heading fontSize="md" lineHeight="1.25" color="gray.800" mb={1}>
            {doc.url ? (
              <Link href={doc.url} isExternal rel="noopener noreferrer">
                {doc.title}
              </Link>
            ) : (
              doc.title
            )}
          </Heading>

          {doc.authors && (
            <Text fontSize="xs" color="text.muted" noOfLines={1} mb={2}>
              {doc.authors}
            </Text>
          )}

          {summary && (
            <Text
              fontSize="sm"
              color="gray.700"
              lineHeight="1.6"
              noOfLines={expanded ? undefined : 3}
            >
              {summary}
            </Text>
          )}

          <HStack spacing={3} mt={3} flexWrap="wrap">
            {doc.url && (
              <Button
                as={Link}
                href={doc.url}
                isExternal
                size="xs"
                colorScheme="gwbi"
                rel="noopener noreferrer"
                _hover={{ textDecoration: "none" }}
              >
                {isPdf
                  ? t("dashboard.open_pdf", "Open the PDF")
                  : t("dashboard.read_paper", "Read the paper")}
              </Button>
            )}
            {doc.abstract && (
              <Button size="xs" variant="link" color="gwbi.600" onClick={onToggle}>
                {expanded
                  ? t("dashboard.show_less", "Show less")
                  : t("dashboard.show_more", "Show more")}
              </Button>
            )}
            {host && (
              <Text fontSize="2xs" color="text.muted">
                {host}
              </Text>
            )}
          </HStack>
        </Box>
      </HStack>
    </Box>
  );
}

const CardFooter = ({
  item,
  onToggleLike,
  onDeletePost,
  onShare,
  onToggleComment,
  commentCount,
}) => (
  <>
    <Divider borderColor="gray.200" />
    <Box px={2} py={2} bg="gray.50">
      <HStack spacing={2} align="center">
        <Button
          onClick={() => onToggleLike(item)}
          size="sm"
          variant="ghost"
          px={2}
        >
          <HStack spacing={2} align="center">
            <Icon
              as={FiHeart}
              color={item.liked_by_me ? "red.500" : "gray.500"}
            />
            <Text>Like</Text>
            <Text fontSize="sm" color="gray.700">
              {Number.isFinite(item.like_count)
                ? item.like_count
                : item.likes || 0}
            </Text>
          </HStack>
        </Button>

        {Array.isArray(item.likers) && item.likers.length > 0 && (
          <AvatarGroup size="xs" max={5} spacing="-0.35rem">
            {item.likers.map((liker, idx) => {
              const key =
                liker?.id ??
                liker?.user_id ??
                liker?.email ??
                liker?.name ??
                `liker-${idx}`;
              return (
                <BrandAvatar
                  key={key}
                  name={liker?.name || ""}
                  src={liker?.avatar_url || undefined}
                />
              );
            })}
          </AvatarGroup>
        )}

        <Button
          size="sm"
          variant="ghost"
          px={2}
          onClick={onToggleComment}
        >
          <HStack spacing={2} align="center">
            <Icon as={FiMessageSquare} />
            <Text>Comment</Text>
            {Number.isFinite(commentCount) && commentCount > 0 && (
              <Text fontSize="sm" color="gray.700">
                {commentCount}
              </Text>
            )}
          </HStack>
        </Button>

        <Button
          size="sm"
          variant="ghost"
          px={2}
          onClick={() => {
            if (typeof onShare === "function") onShare(item);
          }}
        >
          <HStack spacing={2} align="center">
            <Icon as={FiSend} />
            <Text>Share</Text>
          </HStack>
        </Button>

        {/* 👇 Only show Delete if the callback is provided */}
        {onDeletePost && (
          <Button
            size="sm"
            variant="ghost"
            px={2}
            onClick={() => onDeletePost(item)}
          >
            <HStack spacing={2} align="center">
              <Icon as={FiTrash2} />
              <Text>Delete</Text>
            </HStack>
          </Button>
        )}
      </HStack>
    </Box>
  </>
);

export const FeedList = ({
  items,
  totalItems,
  t,
  onToggleLike,
  onDeletePost, // 👈 AGGIUNTO QUI
  currentUserId,
  expandedArticles,
  onToggleArticle,
  onDownloadArticle,
  onShare,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  // A post whose author arrived without an address: look the profile up by id
  // and go there, the same way the member cards elsewhere do.
  const openAuthorById = React.useCallback(
    (userId, event) => {
      event?.stopPropagation?.();
      if (!userId) return;
      openExternalProfile(navigate, userId).catch((err) => {
        console.error("[FeedList] open author profile failed", err);
      });
    },
    [navigate]
  );
  // "@First Last" in a body is only a tag if it names a member, so bodies are
  // rendered against the same list the composer's picker inserted from.
  const [members, setMembers] = React.useState([]);
  React.useEffect(() => {
    let live = true;
    loadUsers().then((list) => {
      if (live) setMembers(list || []);
    });
    return () => {
      live = false;
    };
  }, []);
  const memberIndex = React.useMemo(() => buildMemberIndex(members), [members]);
  const renderBody = React.useCallback(
    (text) =>
      renderTextWithMentions(text, memberIndex, (member, event) =>
        openAuthorById(member.id, event)
      ),
    [memberIndex, openAuthorById]
  );
  const [expandedVideos, setExpandedVideos] = React.useState(() => new Set());
  const [openComments, setOpenComments] = React.useState(() => new Set());
  const commentDraftsRef = React.useRef({});
  const [draftRenderTick, setDraftRenderTick] = React.useState(0);
  const [commentsByItem, setCommentsByItem] = React.useState({});
  const [commentsLoading, setCommentsLoading] = React.useState({});
  const [commentCounts, setCommentCounts] = React.useState({});
  const [interestModalItem, setInterestModalItem] = React.useState(null);
  const [isSubmittingInterest, setIsSubmittingInterest] = React.useState(false);
  const [interestDraft, setInterestDraft] = React.useState({
    motivation: "",
    expertise: "",
    dataContribution: "",
    availability: "1-3 hours/month",
  });


  const resolvedApiKey = React.useMemo(() => {
    if (typeof process !== "undefined" && process?.env) {
      const envKeys = [
        "REACT_APP_API_KEY",
        "NEXT_PUBLIC_API_KEY",
        "VITE_API_KEY",
        "API_KEY",
      ];
      for (const key of envKeys) {
        const value = process.env[key];
        if (value) return value;
      }
    }
    return DEFAULT_HEADERS?.["X-API-KEY"] || "";
  }, []);

  const apiHeaders = React.useCallback(
    (extra = {}) => {
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");
      const uid = getCurrentUserId();
      return {
        "Content-Type": "application/json",
        ...DEFAULT_HEADERS,
        ...(resolvedApiKey ? { "X-API-KEY": resolvedApiKey } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(uid ? { "X-User-Id": String(uid) } : {}),
        ...extra,
      };
    },
    [resolvedApiKey]
  );

  // Hydrate comment counts for all visible items on mount / item change
  React.useEffect(() => {
    if (!items || items.length === 0) return;
    let cancelled = false;
    const run = async () => {
      // Build "type:id" pairs for the batch endpoint
      const pairs = [];
      items.forEach((item, idx) => {
        const itemType = (item.type || "post").toString().trim().toLowerCase();
        const rawId = item.id ?? item.item_id ?? item.post_id;
        const intId = toIntId(rawId);
        if (Number.isFinite(intId)) {
          pairs.push(`${itemType}:${intId}`);
        }
      });
      if (pairs.length === 0) return;
      try {
        const params = new URLSearchParams({ items: pairs.join(",") });
        const res = await fetch(
          `${API_BASE}/analytics/comment-counts?${params.toString()}`,
          { method: "GET", headers: apiHeaders() }
        );
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (!cancelled && data?.counts) {
          setCommentCounts(data.counts);
        }
      } catch {
        /* non-fatal */
      }
    };
    run();
    return () => { cancelled = true; };
  }, [items, apiHeaders]);

  const getLocalUserInfo = React.useCallback(() => {
    const uid = getCurrentUserId();
    if (!uid) return null;
    const bundle = getCachedUserBundle();
    const profile = bundle?.profile;
    const user = bundle?.user;
    const first = profile?.first_name || user?.first_name || "";
    const last = profile?.last_name || user?.last_name || "";
    const name =
      `${first} ${last}`.trim() || user?.email || profile?.email || "Me";
    const avatar_url = profile?.avatar_url || user?.avatar_url || "";
    return {
      id: uid,
      name,
      avatar_url,
    };
  }, []);

  const resetInterestDraft = React.useCallback(() => {
    setInterestDraft({
      motivation: "",
      expertise: "",
      dataContribution: "",
      availability: "1-3 hours/month",
    });
  }, []);

  const openInterestModal = React.useCallback(
    (item) => {
      setInterestModalItem(item || null);
      resetInterestDraft();
    },
    [resetInterestDraft]
  );

  const closeInterestModal = React.useCallback(() => {
    if (isSubmittingInterest) return;
    setInterestModalItem(null);
  }, [isSubmittingInterest]);

  const interestProjectMeta = React.useMemo(
    () => getProjectPublicationMeta(interestModalItem),
    [interestModalItem]
  );

  const handleInterestDraftChange = React.useCallback(
    (field) => (event) => {
      const value = event?.target?.value ?? "";
      setInterestDraft((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const toggleVideoDescription = React.useCallback((id) => {
    if (!id) return;
    setExpandedVideos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const getVideoDescriptionParts = React.useCallback(
    (description, expanded) => {
      if (!description) {
        return { visible: "", isTruncated: false };
      }
      const words = description.trim().split(/\s+/);
      if (expanded || words.length <= 200) {
        return { visible: description, isTruncated: false };
      }
      return {
        visible: words.slice(0, 200).join(" "),
        isTruncated: true,
      };
    },
    []
  );

  const normalizeComment = React.useCallback((c) => {
    if (!c) return null;
    return {
      id: c.id || c.comment_id || c.uuid || c._id || Date.now(),
      text: c.text || c.body || c.comment || "",
      user_id: c.user_id || c.userId || c.user?.id || c.owner_id || null,
      user_name:
        c.user_name ||
        c.user?.name ||
        c.user?.full_name ||
        c.user?.first_name ||
        "",
      avatar_url: c.avatar_url || c.user?.avatar_url || "",
      created_at: c.created_at || c.inserted_at || c.date_created || null,
    };
  }, []);

  const fetchCommentsForItem = React.useCallback(
    async (itemKey, item) => {
      if (!item || !itemKey) return;
      const intId = toIntId(item.id ?? item.item_id ?? item.post_id);
      const itemType = (item.type || "post").toString().trim().toLowerCase();
      if (!Number.isFinite(intId)) {
        console.warn("Invalid content id for comments", { item });
        return;
      }
      setCommentsLoading((prev) => ({ ...prev, [itemKey]: true }));
      try {
        const params = new URLSearchParams({
          type: itemType,
          id: String(intId),
        });
        const res = await fetch(
          `${API_BASE}/analytics/comments?${params.toString()}`,
          {
            method: "GET",
            headers: apiHeaders(),
          }
        );
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`comments GET failed ${res.status}: ${txt}`);
        }
        const payload = await res.json().catch(() => []);
        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.comments)
          ? payload.comments
          : [];
        const normalized = rows
          .map((row) => normalizeComment(row))
          .filter(Boolean);
        setCommentsByItem((prev) => ({
          ...prev,
          [itemKey]: normalized,
        }));
      } catch (err) {
        console.error("fetchCommentsForItem failed", err);
        toast({
          title: "Cannot load comments",
          status: "error",
          description: err.message || String(err),
        });
      } finally {
        setCommentsLoading((prev) => ({ ...prev, [itemKey]: false }));
      }
    },
    [apiHeaders, normalizeComment, toast]
  );

  const toggleCommentBox = React.useCallback(
    (itemKey, item) => {
      if (!itemKey) return;
      setOpenComments((prev) => {
        const next = new Set(prev);
        const willOpen = !next.has(itemKey);
        if (next.has(itemKey)) {
          next.delete(itemKey);
        } else {
          next.add(itemKey);
        }
        if (willOpen && !commentsByItem[itemKey]) {
          fetchCommentsForItem(itemKey, item);
        }
        return next;
      });
    },
    [commentsByItem, fetchCommentsForItem]
  );

  const handleDraftChange = React.useCallback((itemKey, value) => {
    commentDraftsRef.current[itemKey] = value;
  }, []);

  const submitComment = React.useCallback(
    async (item, itemKey) => {
      if (!item || !itemKey) return;
      const text = (commentDraftsRef.current[itemKey] || "").trim();
      if (!text) return;
      const uid = getCurrentUserId();
      if (!uid) {
        toast({
          title: "Please sign in",
          status: "warning",
        });
        return;
      }
      const intId = toIntId(item.id ?? item.item_id ?? item.post_id);
      const itemType = (item.type || "post").toString().trim().toLowerCase();
      if (!Number.isFinite(intId)) {
        toast({
          title: "Invalid content id for comments",
          status: "error",
        });
        return;
      }

      const userInfo = getLocalUserInfo();
      const optimistic = {
        id: `local-${Date.now()}`,
        text,
        user_id: uid,
        user_name: userInfo?.name || "You",
        avatar_url: userInfo?.avatar_url || "",
      };

      setCommentsByItem((prev) => ({
        ...prev,
        [itemKey]: [...(prev[itemKey] || []), optimistic],
      }));
      commentDraftsRef.current[itemKey] = "";
      setDraftRenderTick((n) => n + 1);

      try {
        const res = await fetch(`${API_BASE}/analytics/comments`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({
            type: itemType,
            id: intId,
            comment: text,
            user_id: uid,
            user_name: userInfo?.name,
            avatar_url: userInfo?.avatar_url,
          }),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`comments POST failed ${res.status}: ${txt}`);
        }
        const saved = await res.json().catch(() => null);
        const normalized = normalizeComment(saved) || {
          ...optimistic,
          id: saved?.id || Date.now(),
        };
        setCommentsByItem((prev) => ({
          ...prev,
          [itemKey]: (prev[itemKey] || []).map((c) =>
            c.id === optimistic.id ? normalized : c
          ),
        }));
      } catch (err) {
        console.error("submitComment failed", err);
        setCommentsByItem((prev) => ({
          ...prev,
          [itemKey]: (prev[itemKey] || []).filter((c) => c.id !== optimistic.id),
        }));
        toast({
          title: "Cannot post comment",
          status: "error",
          description: err.message || String(err),
        });
      }
    },
    [apiHeaders, getLocalUserInfo, normalizeComment, toast]
  );

  const submitProjectInterest = React.useCallback(async () => {
    if (!interestModalItem) return;
    const requesterId = Number(getCurrentUserId()) || null;
    if (!requesterId) {
      toast({
        title: "Please sign in",
        status: "warning",
      });
      return;
    }
    if (!interestDraft.motivation.trim()) {
      toast({
        title: "Please add your motivation",
        status: "warning",
      });
      return;
    }

    const ownerId = Number(resolvePostOwnerId(interestModalItem)) || null;
    if (ownerId && String(ownerId) === String(requesterId)) {
      toast({
        title: "You own this project",
        description: "You cannot apply to your own project.",
        status: "info",
      });
      return;
    }

    const postId = toIntId(
      interestModalItem.id ?? interestModalItem.item_id ?? interestModalItem.post_id
    );
    if (!Number.isFinite(postId)) {
      toast({
        title: "Cannot submit interest",
        description: "Project post id is invalid.",
        status: "error",
      });
      return;
    }

    setIsSubmittingInterest(true);
    try {
      const requesterInfo = getLocalUserInfo();
      const interestMessage = createInterestMessage({
        projectMeta: interestProjectMeta,
        draft: interestDraft,
        applicantName: requesterInfo?.name || "GWBI Member",
      });
      const trimmedMotivation = interestDraft.motivation.trim();
      const trimmedExpertise = interestDraft.expertise.trim();
      const trimmedContribution = interestDraft.dataContribution.trim();
      const trimmedAvailability = interestDraft.availability.trim();

      let persistedToProjectInterests = false;
      let projectInterestsError = "";
      try {
        const interestRes = await fetch(`${API_BASE}/project-interests`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({
            project_post_id: postId,
            motivation: trimmedMotivation,
            expertise: trimmedExpertise || null,
            data_contribution: trimmedContribution || null,
            availability: trimmedAvailability || null,
            message: interestMessage,
          }),
        });
        if (interestRes.ok) {
          persistedToProjectInterests = true;
        } else {
          const txt = await interestRes.text().catch(() => "");
          projectInterestsError = `project-interests ${interestRes.status}: ${txt}`;
        }
      } catch (err) {
        projectInterestsError = err?.message || String(err);
      }

      const commentRes = await fetch(`${API_BASE}/analytics/comments`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify({
          type: "post",
          id: postId,
          comment: interestMessage,
          user_id: requesterId,
          user_name: requesterInfo?.name,
          avatar_url: requesterInfo?.avatar_url,
        }),
      });
      if (!commentRes.ok && !persistedToProjectInterests) {
        const txt = await commentRes.text().catch(() => "");
        throw new Error(
          `Interest submission failed (${commentRes.status}): ${txt}${
            projectInterestsError ? ` | ${projectInterestsError}` : ""
          }`
        );
      }

      let dmSent = false;
      if (ownerId && ownerId !== requesterId) {
        let threadId = null;
        const startRes = await fetch(`${API_BASE}/dm/threads`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({
            user_id: requesterId,
            other_user_id: ownerId,
          }),
        });

        if (startRes.ok) {
          const startData = await startRes.json().catch(() => ({}));
          threadId = startData?.thread_id || null;
        } else {
          const listRes = await fetch(
            `${API_BASE}/dm/threads?user_id=${encodeURIComponent(requesterId)}`,
            {
              method: "GET",
              headers: apiHeaders(),
            }
          );
          if (listRes.ok) {
            const rows = await listRes.json().catch(() => []);
            if (Array.isArray(rows)) {
              const match = rows.find(
                (row) => String(row?.other_user_id || "") === String(ownerId)
              );
              threadId = match?.thread_id || null;
            }
          }
        }

        if (threadId) {
          const dmBody = [
            `New interest on your project: ${interestProjectMeta?.title || "Collaborative Project"}`,
            `Applicant: ${requesterInfo?.name || "GWBI Member"}`,
            `Motivation: ${interestDraft.motivation.trim()}`,
          ].join("\n");
          const dmRes = await fetch(`${API_BASE}/dm/threads/${threadId}/messages`, {
            method: "POST",
            headers: apiHeaders(),
            body: JSON.stringify({
              sender_id: requesterId,
              body: dmBody,
            }),
          });
          dmSent = dmRes.ok;
        }
      }

      if (projectInterestsError && !persistedToProjectInterests) {
        debugLog("project-interests fallback", projectInterestsError);
      }

      toast({
        title: "Interest submitted",
        description: persistedToProjectInterests
          ? dmSent
            ? "Saved and owner has been notified."
            : "Saved. Owner notification is pending."
          : dmSent
          ? "Saved to project activity and owner has been notified."
          : "Saved to project activity. Owner notification is pending.",
        status: persistedToProjectInterests ? "success" : "warning",
      });
      closeInterestModal();
    } catch (err) {
      toast({
        title: "Cannot submit interest",
        description: err.message || String(err),
        status: "error",
      });
    } finally {
      setIsSubmittingInterest(false);
    }
  }, [
    apiHeaders,
    closeInterestModal,
    getLocalUserInfo,
    interestDraft,
    interestModalItem,
    interestProjectMeta,
    toast,
  ]);

  const CommentThread = ({
    isOpen,
    comments,
    draft,
    draftKey,
    onDraftChange,
    onSubmit,
    totalCount,
    isLoading,
  }) => {
    if (!isOpen) return null;
    return (
      <Box px={4} pb={4}>
        <Text fontWeight="semibold" mb={2}>
          {t("dashboard.comments", "Comments")}
          {Number.isFinite(totalCount) && totalCount > 0
            ? ` (${totalCount})`
            : ""}
        </Text>
        {isLoading && (
          <Text fontSize="sm" color="gray.500" mb={2}>
            {t("dashboard.loading_comments", "Loading comments...")}
          </Text>
        )}
        {comments.length === 0 && !isLoading ? (
          <Text fontSize="sm" color="gray.500" mb={2}>
            {t("dashboard.no_comments", "No comments yet")}
          </Text>
        ) : (
          <VStack align="stretch" spacing={2} mb={2}>
            {comments.map((c) => (
              <HStack
                key={c.id}
                p={2}
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
                bg="gray.50"
                align="flex-start"
                spacing={3}
              >
                <BrandAvatar
                  size="sm"
                  name={c.user_name || "User"}
                  src={c.avatar_url || undefined}
                />
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                    {c.user_name || t("dashboard.member", "Member")}
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    {renderBody(c.text)}
                  </Text>
                  {c.created_at && (
                    <Text fontSize="xs" color="gray.500">
                      {formatFeedDate(c.created_at)}
                    </Text>
                  )}
                </Box>
              </HStack>
            ))}
          </VStack>
        )}
        <HStack align="flex-start" spacing={2}>
          <MentionTextarea
            key={draftKey}
            defaultValue={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            placeholder={t("dashboard.add_comment", "Add a comment...")}
            size="sm"
            bg="white"
            flex="1"
          />
          <Button size="sm" colorScheme="gwbi" onClick={onSubmit}>
            {t("dashboard.post_comment", "Post")}
          </Button>
        </HStack>
      </Box>
    );
  };

  return (
    <VStack spacing={6} align="stretch">
      {items.length === 0 ? (
        <Box
          borderRadius="md"
          overflow="hidden"
          bg="white"
          color="gray.800"
          boxShadow="md"
          p={6}
        >
          <Heading size="sm" mb={2}>
            {totalItems === 0
              ? t("dashboard.loading_feed")
              : t("dashboard.no_matches")}
          </Heading>
        </Box>
      ) : (
        items.map((item, index) => {
          const key = makeKey(item, index);
          const meta = typeMeta(item.type);
          const videoKey =
            item.type === "video"
              ? item.id ||
                item.video_id ||
                item.guid ||
                item.url ||
                item.link ||
                item.title ||
                key
              : null;
          const isVideoExpanded =
            item.type === "video" && videoKey
              ? expandedVideos.has(videoKey)
              : false;
          const videoDescription =
            item.type === "video"
              ? getVideoDescriptionParts(item.description, isVideoExpanded)
              : { visible: "", isTruncated: false };
          const commentKey = key;
          const isCommentOpen = openComments.has(commentKey);
          const itemComments = commentsByItem[commentKey] || [];
          // Comment count priority:
          // 1. If comments were fetched locally, use that length
          // 2. If the item already has comment_count from backend, use it
          // 3. Look up the batch-hydrated count from /analytics/comment-counts
          const itemType = (item.type || "post").toString().trim().toLowerCase();
          const intId = toIntId(item.id ?? item.item_id ?? item.post_id);
          const hydratedKey = Number.isFinite(intId) ? `${itemType}:${intId}` : null;
          const baseCommentCount = Number.isFinite(item.comment_count) && item.comment_count > 0
            ? item.comment_count
            : (hydratedKey && commentCounts[hydratedKey]) || 0;
          const totalCommentCount = commentsByItem[commentKey]
            ? itemComments.length
            : baseCommentCount;
          const draftValue = commentDraftsRef.current[commentKey] || "";
          const draftKey = `${commentKey}:${draftRenderTick}`;
          const projectPublicationMeta = getProjectPublicationMeta(item);
          const isProjectPublication = !!projectPublicationMeta;
          const itemOwnerId = resolvePostOwnerId(item);
          const isOwnProjectPublication =
            isProjectPublication &&
            itemOwnerId != null &&
            currentUserId != null &&
            String(itemOwnerId) === String(currentUserId);
          if (item.type === "post") {
            debugLog("post ownership check", {
              key,
              postId: item.id,
              ownerId: itemOwnerId,
              currentUserId,
              postSubtype: item.post_subtype,
            });
          }

const isUuid = (v) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(v || "")
  );

// prende public_id da dove arriva davvero
const publicId =
  item?.author_public_id ||
  item?.author?.public_id ||
  item?.author?.publicId ||
  item?.user?.public_id ||
  null;

const authorSlug =
  item?.author_profile_slug || item?.author?.profile_slug || null;
const profileTo =
  authorSlug || (publicId && isUuid(publicId))
    ? profilePath({ profile_slug: authorSlug, public_id: publicId })
    : null;
// No address on the row -- an uploaded paper, say -- but we do know who wrote
// it, which is enough to fetch the address on the way there.
const authorFallbackId = profileTo ? null : itemOwnerId;
const authorIsClickable = Boolean(profileTo || authorFallbackId);

          return (
            <Box
  key={key}
  borderRadius="md"
  overflow="hidden"
  bg="white"
  color="gray.800"
  boxShadow={isProjectPublication ? "lg" : "md"}
  borderWidth={isProjectPublication ? "2px" : "0"}
  borderColor={isProjectPublication ? "teal.300" : "transparent"}
  w="full"              // ✅ prende tutta la larghezza disponibile
  maxW="900px"          // ✅ ma non supera 900px
  mx="auto"             // ✅ centrata quando arriva a 900px
  alignSelf="stretch"   // ✅ evita shrink strani
  display="flex"
  flexDirection="column"
  minW="0"              // ✅ evita overflow/shrink con flex
>

              {item.type === "news" ? (
                <>
                  <Box px={4} pt={3} pb={0}>
                    <HStack spacing={1} color="gray.500" align="center">
                      <Icon as={meta.icon} boxSize={3} />
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        textTransform="uppercase"
                      >
                        {t(meta.label)}
                      </Text>
                    </HStack>
                  </Box>
                  <Box p={4}>
                    <HStack spacing={2} mb={2} align="center">
                      <Image
                        src={`/${(item?.source || "generic")
                          .toString()
                          .replace(/[^a-z0-9]/gi, "")
                          .toLowerCase()}.png`}
                        alt={item?.source || "News source"}
                        boxSize="32px"
                        objectFit="contain"
                        onError={(e) => {
                          e.currentTarget.src = "/generic.png";
                        }}
                      />
                      <Box fontSize="sm" fontWeight="bold" color="gray.800">
                        {(item.source ?? "").replace(/([A-Z])/g, " $1").trim()}
                      </Box>
                    </HStack>
                    <Text
                      fontWeight="bold"
                      mb={2}
                      {...(item.link
                        ? {
                            onClick: () => {
                              window.open(
                                item.link,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            },
                            cursor: "pointer",
                            _hover: { textDecoration: "underline" },
                          }
                        : {})}
                    >
                      {item.title}
                    </Text>
                    <Divider borderColor="gray.300" mb={2} />
                    <Text fontSize="sm" color="gray.500">
                      {formatFeedDate(item.date_created)}
                    </Text>
                  </Box>
                  {item.image && item.link ? (
                    <Box
                      as="button"
                      onClick={() => {
                        window.open(item.link, "_blank", "noopener,noreferrer");
                      }}
                      style={{
                        padding: 0,
                        border: 0,
                        background: "transparent",
                      }}
                    >
                      <Image
                        src={item?.image || "/news_fallback.jpg"}
                        onError={(e) => {
                          e.currentTarget.src = "/news_fallback.jpg";
                        }}
                        alt={item?.title || t("dashboard.news")}
                        width="100%"
                        height="auto"
                        display="block"
                        cursor="pointer"
                      />
                    </Box>
                  ) : (
                    item.image && (
                      <Image
                        src={item?.image || "/news_fallback.jpg"}
                        onError={(e) => {
                          e.currentTarget.src = "/news_fallback.jpg";
                        }}
                        alt={item?.title || t("dashboard.news")}
                        width="100%"
                        height="auto"
                        display="block"
                      />
                    )
                  )}
                  <Box p={4}>
                    <Text fontSize="sm" color="gray.700">
                      {item.text?.split(" ").slice(0, 40).join(" ") || ""}
                      {item.text?.split(" ").length > 40 && (
                        <Text
                          as="button"
                          color="gwbi.500"
                          fontWeight="semibold"
                          ml={1}
                          onClick={() => {
                            window.open(
                              item.link,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                        >
                          {t("dashboard.more")}
                        </Text>
                      )}
                    </Text>
                  </Box>
                  <CardFooter
                    item={item}
                    onToggleLike={onToggleLike}
                    onShare={onShare}
                    onToggleComment={() => toggleCommentBox(commentKey, item)}
                    commentCount={totalCommentCount}
                  />
                  <CommentThread
                    isOpen={isCommentOpen}
                    comments={itemComments}
                    draft={draftValue}
                    draftKey={draftKey}
                    onDraftChange={(val) => handleDraftChange(commentKey, val)}
                    onSubmit={() => submitComment(item, commentKey)}
                    totalCount={totalCommentCount}
                    isLoading={!!commentsLoading[commentKey]}
                  />
                </>
              ) : item.type === "conference" ? (
                <>
                  <Box px={4} pt={3} pb={0}>
                    <HStack spacing={2} color="gray.500" align="center">
                      <Icon as={meta.icon} boxSize={3} />
                      <Text fontSize="xs" fontWeight="bold">
                        {(() => {
                          const focus = (item.focus || "")
                            .toString()
                            .trim()
                            .toUpperCase();
                          return focus && focus !== "EVENT"
                            ? `${focus}`
                            : t("dashboard.event").toUpperCase();
                        })()}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatFeedDate(item.date_conference)}
                      </Text>
                    </HStack>
                  </Box>
                  <Box p={4}>
                    <HStack spacing={2} mb={2}>
                      <Box
                        fontSize="sm"
                        fontWeight="bold"
                        color="gray.800"
                        {...(getLink(item)
                          ? {
                              onClick: () => {
                                window.open(
                                  getLink(item),
                                  "_blank",
                                  "noopener,noreferrer"
                                );
                              },
                              cursor: "pointer",
                              _hover: { textDecoration: "underline" },
                            }
                          : {})}
                      >
                        {item.name || item.title || t("dashboard.conference")}
                      </Box>
                    </HStack>
                  </Box>
                  {getLink(item) ? (
                    <Box
                      as="button"
                      onClick={() => {
                        window.open(
                          getLink(item),
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                      style={{
                        padding: 0,
                        border: 0,
                        background: "transparent",
                      }}
                    >
                      <Image
                        src={item.image}
                        alt={
                          item.name || item.title || t("dashboard.conference")
                        }
                        width="100%"
                        height="auto"
                        display="block"
                        cursor="pointer"
                      />
                    </Box>
                  ) : (
                    <Image
                      src={item.image}
                      alt={item.name || item.title || t("dashboard.conference")}
                      width="100%"
                      height="auto"
                      display="block"
                    />
                  )}
                  <Box p={4}>
                    <Text fontWeight="bold">
                      {item.location || item.city || "—"}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {formatFeedDate(item.date_created || item.updated_at)}
                    </Text>
                  </Box>
                  <CardFooter
                    item={item}
                    onToggleLike={onToggleLike}
                    onShare={onShare}
                    onToggleComment={() => toggleCommentBox(commentKey, item)}
                    commentCount={totalCommentCount}
                  />
                  <CommentThread
                    isOpen={isCommentOpen}
                    comments={itemComments}
                    draft={draftValue}
                    draftKey={draftKey}
                    onDraftChange={(val) => handleDraftChange(commentKey, val)}
                    onSubmit={() => submitComment(item, commentKey)}
                    totalCount={totalCommentCount}
                    isLoading={!!commentsLoading[commentKey]}
                  />
                </>
              ) : item.type === "publication" ? (
                <>
                  <Box px={4} pt={3} pb={0}>
                    <HStack spacing={1} color="gray.500" align="center">
                      <Icon as={meta.icon} boxSize={3} />
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        textTransform="uppercase"
                      >
                        {t(meta.label)}
                      </Text>
                    </HStack>
                  </Box>
                  <Box p={4}>
                    <HStack spacing={2} mb={2}>
                      <Image
                        src={getPublicationLogo(item)}
                        alt={item.journal || item.publisher || "Journal"}
                        boxSize="32px"
                        objectFit="contain"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = JOURNAL_GENERIC_ICON;
                        }}
                      />

                      <Box fontSize="sm" fontWeight="bold" color="gray.800">
                        {item.journal || item.publisher || "—"}
                      </Box>
                    </HStack>
                    <Divider borderColor="gray.300" mb={4} />
                    <Text
                      fontWeight="bold"
                      {...(item.url || item.link
                        ? {
                            onClick: () => {
                              window.open(
                                item.url || item.link,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            },
                            cursor: "pointer",
                            _hover: { textDecoration: "underline" },
                          }
                        : {})}
                    >
                      {item.title || t("dashboard.publication")}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {item.date_created
                        ? formatFeedDate(item.date_created)
                        : item.date_published
                        ? formatFeedDate(item.date_published)
                        : t("dashboard.date_unknown")}
                    </Text>
                  </Box>
                  <Box p={4}>
                    <Text fontSize="sm" color="gray.700">
                      {item.abstract?.split(" ").slice(0, 40).join(" ") || ""}
                      {item.abstract?.split(" ").length > 40 && (
                        <Text
                          as="button"
                          color="gwbi.500"
                          fontWeight="semibold"
                          ml={1}
                          onClick={() => {
                            window.open(
                              item.url || item.link,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                        >
                          {t("dashboard.more")}
                        </Text>
                      )}
                    </Text>
                  </Box>
                  <Box p={4}>
                    <Text fontSize="sm" fontStyle="italic" color="gray.600">
                      {t("dashboard.insight_prefix")}{" "}
                      {item.insights || item.insight || "—"}
                    </Text>
                  </Box>
                  <CardFooter
                    item={item}
                    onToggleLike={onToggleLike}
                    onShare={onShare}
                    onToggleComment={() => toggleCommentBox(commentKey, item)}
                    commentCount={totalCommentCount}
                  />
                  <CommentThread
                    isOpen={isCommentOpen}
                    comments={itemComments}
                    draft={draftValue}
                    draftKey={draftKey}
                    onDraftChange={(val) => handleDraftChange(commentKey, val)}
                    onSubmit={() => submitComment(item, commentKey)}
                    totalCount={totalCommentCount}
                    isLoading={!!commentsLoading[commentKey]}
                  />
                </>
              ) : item.type === "video" ? (
                <>
                  <Box px={4} pt={3} pb={0}>
                    <HStack spacing={2} color="gray.500" align="center">
                      <Icon as={meta.icon} boxSize={3} />
                      <Text fontSize="xs" fontWeight="bold">
                        {t(meta.label)}
                      </Text>
                    </HStack>
                  </Box>
                  <Box p={4}>
                    <Heading size="sm" mb={2}>
                      {item.title || t("dashboard.video_label")}
                    </Heading>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      {item.channel_title || "YouTube"} ·{" "}
                      {formatFeedDate(item.date_created)}
                    </Text>
                    {item.thumbnail_url && (
                      <Box
                        as="button"
                        onClick={() => {
                          if (item.link) {
                            window.open(
                              item.link,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }
                        }}
                        style={{
                          padding: 0,
                          border: 0,
                          background: "transparent",
                        }}
                        cursor={item.link ? "pointer" : "default"}
                        width="100%"
                        maxW="100%"
                        display="block"
                        overflow="hidden"
                      >
                        <Image
                          src={item.thumbnail_url}
                          alt={item.title}
                          borderRadius="md"
                          mb={3}
                          width="100%"
                          maxW="100%"
                          objectFit="cover"
                          _hover={
                            item.link
                              ? { opacity: 0.95, transform: "translateY(-1px)" }
                              : undefined
                          }
                          transition="all 0.1s ease-in-out"
                        />
                      </Box>
                    )}
                    {item.description && (
                      <Box>
                        <Text fontSize="sm" color="gray.700">
                          {videoDescription.visible}
                          {!isVideoExpanded && videoDescription.isTruncated
                            ? "..."
                            : ""}
                        </Text>
                        {videoDescription.isTruncated && (
                          <Button
                            size="sm"
                            variant="link"
                            color="gwbi.500"
                            mt={1}
                            onClick={() => toggleVideoDescription(videoKey)}
                          >
                            {isVideoExpanded
                              ? t("dashboard.show_less", "Show less")
                              : t("dashboard.more", "...more")}
                          </Button>
                        )}
                      </Box>
                    )}
                    {item.link && (
                      <Button
                        as="a"
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        variant="ghost"
                        mt={2}
                    >
                      {t("dashboard.watch_video")}
                    </Button>
                  )}
                </Box>
                <CardFooter
                  item={item}
                  onToggleLike={onToggleLike}
                  onShare={onShare}
                  onToggleComment={() => toggleCommentBox(commentKey, item)}
                  commentCount={totalCommentCount}
                />
                <CommentThread
                  isOpen={isCommentOpen}
                  comments={itemComments}
                  draft={draftValue}
                  draftKey={draftKey}
                  onDraftChange={(val) => handleDraftChange(commentKey, val)}
                  onSubmit={() => submitComment(item, commentKey)}
                  totalCount={totalCommentCount}
                  isLoading={!!commentsLoading[commentKey]}
                />
              </>
            ) : item.type === "notice" ? (
              <Box p={4}>
                <HStack spacing={2} color="gray.500" align="center" mb={2}>
                  <Icon as={meta.icon} boxSize={3} />
                  <Text fontSize="xs" fontWeight="bold">
                    {t(meta.label)}
                  </Text>
                </HStack>
                <Heading size="sm" mb={1}>
                  {item.title}
                </Heading>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {[item.institution, formatFeedDate(item.date_created)]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
                {item.description && (
                  <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">
                    {item.description.length > 320
                      ? `${item.description.slice(0, 320).trimEnd()}…`
                      : item.description}
                  </Text>
                )}
                <HStack mt={3} spacing={3} flexWrap="wrap">
                  {item.expires_on && (
                    <Tag size="sm" colorScheme="gray" borderRadius="full">
                      <TagLabel fontSize="0.65rem">
                        {t("dashboard.notice_expires", {
                          date: formatFeedDate(item.expires_on),
                        })}
                      </TagLabel>
                    </Tag>
                  )}
                  <Link
                    as={RouterLink}
                    to="/noticeboard"
                    fontSize="sm"
                    color="gwbi.600"
                    fontWeight="semibold"
                  >
                    {t("dashboard.notice_open_board")}
                  </Link>
                </HStack>
              </Box>
            ) : (
                <>
<Box px={4} pt={3} pb={0}>
  <HStack spacing={3} align="center">
    <BrandAvatar
      size="sm"
      name={`${item.author_first_name || ""} ${item.author_last_name || ""}`.trim()}
      src={item.author_avatar_url || undefined}
      as={profileTo ? RouterLink : undefined}
      to={profileTo || undefined}
      onClick={
        authorFallbackId ? (e) => openAuthorById(authorFallbackId, e) : undefined
      }
      cursor={authorIsClickable ? "pointer" : "default"}
    />

    <VStack spacing={0} align="start">
      <Link
        as={profileTo ? RouterLink : undefined}
        to={profileTo || undefined}
        onClick={
          authorFallbackId ? (e) => openAuthorById(authorFallbackId, e) : undefined
        }
        cursor={authorIsClickable ? "pointer" : "default"}
        fontSize="sm"
        fontWeight="bold"
        color="gray.800"
        _hover={{ textDecoration: authorIsClickable ? "underline" : "none" }}
      >
        {`${item.author_first_name || ""} ${item.author_last_name || ""}`.trim() ||
          t("dashboard.member", "Member")}
      </Link>

      <HStack spacing={2} align="center">
        <Text fontSize="xs" color="gray.500">
          {formatFeedDate(item.date_created || item.created_at)}
        </Text>
        {item.scope && item.team_key && (
          <Tag size="sm" colorScheme={item.scope === "region" ? "blue" : "purple"} borderRadius="full" variant="subtle">
            <TagLabel fontSize="0.65rem">{item.scope === "region" ? "Region" : "Theme"}: {item.team_key}</TagLabel>
          </Tag>
        )}
      </HStack>
    </VStack>
  </HStack>
</Box>




                  <Box p={4}>
                    {projectPublicationMeta ? (
                      <Box
                        borderWidth="1px"
                        borderColor="teal.200"
                        bg="teal.50"
                        borderRadius="md"
                        p={4}
                      >
                        <HStack justify="space-between" align="center" mb={3} flexWrap="wrap" spacing={2}>
                          <Tag size="sm" colorScheme="gwbi" borderRadius="full">
                            <HStack spacing={1}>
                              <Icon as={FiFlag} boxSize={3} />
                              <TagLabel>Project Publication</TagLabel>
                            </HStack>
                          </Tag>
                          <Text fontSize="xs" color="teal.700" fontWeight="medium">
                            Collaborative Project Builder
                          </Text>
                        </HStack>
                        <Heading size="sm" color="teal.800" mb={2}>
                          {projectPublicationMeta.title}
                        </Heading>
                        {projectPublicationMeta.summary && (
                          <Text fontSize="sm" color="gray.700" mb={3}>
                            {projectPublicationMeta.summary}
                          </Text>
                        )}
                        <Box
                          mb={3}
                          px={3}
                          py={2}
                          borderRadius="md"
                          bg="teal.100"
                          borderWidth="1px"
                          borderColor="teal.300"
                        >
                          <HStack spacing={2} align="center">
                            <Text as="span" fontWeight="bold" color="teal.900" fontSize="xs" textTransform="uppercase">
                              Theme
                            </Text>
                            <Tag size="sm" colorScheme="gwbi" variant="solid" borderRadius="full">
                              <TagLabel>{projectPublicationMeta.theme || "Not specified"}</TagLabel>
                            </Tag>
                          </HStack>
                        </Box>
                        <VStack spacing={1} align="stretch">
                          <Text fontSize="sm" color="gray.700">
                            <Text as="span" fontWeight="semibold" color="teal.800">
                              Regional Cluster:
                            </Text>{" "}
                            {projectPublicationMeta.regionalCluster || "Global / Multi-region"}
                          </Text>
                          <Text fontSize="sm" color="gray.700">
                            <Text as="span" fontWeight="semibold" color="teal.800">
                              Scope:
                            </Text>{" "}
                            {projectPublicationMeta.scope || "Not specified"}
                          </Text>
                          <Text fontSize="sm" color="gray.700">
                            <Text as="span" fontWeight="semibold" color="teal.800">
                              Track:
                            </Text>{" "}
                            {projectPublicationMeta.track || "Not specified"}
                          </Text>
                          {projectPublicationMeta.routing && (
                            <Text fontSize="sm" color="gray.700">
                              <Text as="span" fontWeight="semibold" color="teal.800">
                                Routing:
                              </Text>{" "}
                              {projectPublicationMeta.routing}
                            </Text>
                          )}
                        </VStack>
                        <HStack mt={4} spacing={3} flexWrap="wrap">
                          {isOwnProjectPublication ? (
                            <Tag size="sm" colorScheme="gray" borderRadius="full">
                              <TagLabel>Project Owner</TagLabel>
                            </Tag>
                          ) : (
                            <Button
                              size="sm"
                              colorScheme="gwbi"
                              onClick={() => openInterestModal(item)}
                            >
                              I&apos;m Interested
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleCommentBox(commentKey, item)}
                          >
                            View Responses
                          </Button>
                        </HStack>
                      </Box>
                    ) : item.document ? (
                      // A paper announcement. The body says the same thing in
                      // plain text -- "New paper added: ...", the authors, the
                      // link -- so showing both would be saying it twice.
                      <PaperCard
                        doc={item.document}
                        expanded={expandedArticles.has(item.id)}
                        onToggle={() => onToggleArticle(item.id)}
                        t={t}
                      />
                    ) : (
                      item.body && (
                      <Text
                        fontSize="md"
                        color="gray.800"
                        whiteSpace="pre-wrap"
                      >
                        {renderBody(item.body)}
                      </Text>
                      )
                    )}
                  </Box>
                  {item.media_url && item.media_type === "image" && (
                    <Image
                      src={item.media_url}
                      alt="post media"
                      width="100%"
                      height="auto"
                      display="block"
                    />
                  )}
                  {item.media_url && item.media_type === "video" && (
                    <AspectRatio ratio={16 / 9}>
                      <video
                        src={item.media_url}
                        controls
                        style={{ width: "100%", height: "100%" }}
                      />
                    </AspectRatio>
                  )}
                  {item.post_subtype === "uploaded_article" && (
                    <Box p={4}>
                      <VStack align="stretch" spacing={2}>
                        <Text fontWeight="bold">{item.title}</Text>
                        {item.abstract && (
                          <Text
                            fontSize="sm"
                            color="gray.600"
                            noOfLines={
                              expandedArticles.has(item.id) ? undefined : 3
                            }
                          >
                            {item.abstract}
                          </Text>
                        )}
                        <Button
                          size="sm"
                          variant="link"
                          color="gwbi.500"
                          onClick={() => onToggleArticle(item.id)}
                          alignSelf="flex-start"
                        >
                          {expandedArticles.has(item.id)
                            ? t("dashboard.show_less")
                            : t("dashboard.show_more")}
                        </Button>
                        {item.download_url && (
                          <Button
                            size="sm"
                            onClick={() => onDownloadArticle(item.download_url)}
                          >
                            {t("dashboard.download")}
                          </Button>
                        )}
                      </VStack>
                    </Box>
                  )}

                  <CardFooter
                    item={item}
                    onToggleLike={onToggleLike}
                    onDeletePost={(() => {
                      if (
                        !onDeletePost ||
                        !currentUserId ||
                        item.type !== "post" ||
                        item.post_subtype
                      ) {
                        return undefined;
                      }
                      const ownerId =
                        item.user_id ||
                        item.userId ||
                        item.user?.id ||
                        item.author_id ||
                        item.owner_id ||
                        null;
                      if (
                        ownerId != null &&
                        String(ownerId) === String(currentUserId)
                      ) {
                        return onDeletePost;
                      }
                    return undefined;
                  })()}
                  onShare={onShare}
                  onToggleComment={() => toggleCommentBox(commentKey, item)}
                  commentCount={totalCommentCount}
                />
                <CommentThread
                  isOpen={isCommentOpen}
                  comments={itemComments}
                  draft={draftValue}
                  draftKey={draftKey}
                  onDraftChange={(val) => handleDraftChange(commentKey, val)}
                  onSubmit={() => submitComment(item, commentKey)}
                  totalCount={totalCommentCount}
                  isLoading={!!commentsLoading[commentKey]}
                />
              </>
            )}
            </Box>
          );
        })
      )}
      <Modal
        isOpen={!!interestModalItem}
        onClose={closeInterestModal}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Express Interest
            {interestProjectMeta?.title ? `: ${interestProjectMeta.title}` : ""}
          </ModalHeader>
          <ModalCloseButton isDisabled={isSubmittingInterest} />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Why are you interested?</FormLabel>
                <Textarea
                  value={interestDraft.motivation}
                  onChange={handleInterestDraftChange("motivation")}
                  placeholder="Describe fit with this project and expected contribution."
                  minH="100px"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Relevant expertise</FormLabel>
                <Input
                  value={interestDraft.expertise}
                  onChange={handleInterestDraftChange("expertise")}
                  placeholder="Example: panel econometrics, trade policy, demand modeling"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Data or methods contribution</FormLabel>
                <Input
                  value={interestDraft.dataContribution}
                  onChange={handleInterestDraftChange("dataContribution")}
                  placeholder="Datasets, methods, software, partner access..."
                />
              </FormControl>
              <FormControl>
                <FormLabel>Availability</FormLabel>
                <Input
                  value={interestDraft.availability}
                  onChange={handleInterestDraftChange("availability")}
                  placeholder="Example: 4-6 hours/month"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={closeInterestModal}
              isDisabled={isSubmittingInterest}
            >
              Cancel
            </Button>
            <Button
              colorScheme="gwbi"
              onClick={submitProjectInterest}
              isLoading={isSubmittingInterest}
              loadingText="Sending"
            >
              Send Interest
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};
